import { withChildren, BuilderBlocks, type BuilderElement } from '@builder.io/react';
import React from 'react';
import { twMerge } from 'tailwind-merge';

import { Text, Button, ButtonProps } from '@videoblocks/design-system';

import { BuilderState } from '../types/shared';

export interface FooterHookProps {
    headline: string;
    content: string;
    showCallToAction: boolean;
    callToAction: ButtonProps & { text: string };
    shadowVariant: boolean;
    backgroundColor?: string;
    shadowOptions?:
        | {
              shadowColor: string;
              shadowImage: string;
          }
        | undefined;
    dynamicContent: {
        blocks: React.ReactNode[];
    };
    builderBlock: BuilderElement;
    builderState?: BuilderState;
}

const FooterHook = ({
    headline,
    showCallToAction,
    callToAction,
    shadowVariant,
    shadowOptions,
    backgroundColor,
    dynamicContent = {
        blocks: [],
    },
    builderBlock,
    builderState = {},
}: FooterHookProps) => {
    const { shadowColor, shadowImage } = shadowOptions || {};
    const isMobile = builderState?.state?.deviceSize !== 'large';
    const contentClass = twMerge('relative', shadowVariant && 'w-[calc(100%-2rem)] left-6');
    const { blocks } = dynamicContent;
    return (
        <div className={twMerge('px-6 py-16 md:px-12 md:py-24 lg:p-24', shadowVariant && 'pr-4 md:pr-12 lg:pr-32')}>
            <div className={contentClass}>
                {shadowVariant ? (
                    <div className="absolute -inset-0">
                        <div
                            className="absolute w-full h-full -left-6 top-6 rounded-xl"
                            style={{
                                backgroundColor: shadowColor || undefined,
                                backgroundImage: shadowImage ? `url(${shadowImage})` : undefined,
                                backgroundSize: shadowImage && 'cover',
                            }}
                            role="presentation"
                        />
                    </div>
                ) : null}
                <div
                    className="relative px-6 py-12 md:p-12 md:gap-4 lg:p-16 rounded-xl"
                    style={
                        !isMobile && !shadowVariant
                            ? { backgroundColor: 'var(--color-white, #ffffff)' }
                            : { backgroundColor }
                    }
                >
                    <div className="flex flex-col gap-4 lg:gap-8 lg:flex-row">
                        <Text
                            fontFamily="polysans"
                            fontSize="4xl"
                            fontWeight="semibold"
                            className="flex-1 text-gray-900"
                        >
                            {headline}
                        </Text>
                        <div className="flex-1 lg:p-8 lg:rounded-xl" style={!isMobile ? { backgroundColor } : {}}>
                            <BuilderBlocks
                                parentElementId={builderBlock.id}
                                dataPath={`dynamicContent.blocks`}
                                blocks={blocks}
                            />
                            {showCallToAction ? (
                                <Button
                                    href={callToAction.href}
                                    variant={callToAction.variant}
                                    target={callToAction.target}
                                    className="mt-8"
                                    size="large"
                                >
                                    {callToAction.text}
                                </Button>
                            ) : null}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default withChildren(FooterHook);
